import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAgentDetail, useAgentDelegations, useAgentAuditLog, useAgentCredentials, useAgentDelegationChain } from '../../api/hooks';
import { apiFetch } from '../../api/client';
import { encodePathSegment } from '../../lib/url';
import { describeTxError } from '../../lib/txError';
import Modal from '../../components/Modal';
import { useWeb3 } from '../../contexts/Web3Context';
import { useNetwork } from '../../contexts/NetworkContext';
import { useAgentTrustRegistry, readOnChainAgent, type OnChainAgent } from '../../hooks/useAgentTrustRegistry';
import AgentSuspendControl from './AgentSuspendControl';
import NewDelegationDialog from './NewDelegationDialog';

interface AgentCapability {
  name: string;
  description: string;
  version?: string;
}

const statusConfig: Record<string, { dot: string; text: string; bg: string }> = {
  active: { dot: 'bg-emerald-500 animate-pulse', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  suspended: { dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50' },
  decommissioned: { dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50' },
  registered: { dot: 'bg-slate-400', text: 'text-slate-600', bg: 'bg-slate-50' },
};

const autonomyConfig: Record<string, { bg: string; text: string; bar: string }> = {
  Intern: { bg: 'bg-slate-100', text: 'text-slate-700', bar: 'bg-slate-400' },
  Junior: { bg: 'bg-blue-50', text: 'text-blue-700', bar: 'bg-blue-500' },
  Senior: { bg: 'bg-emerald-50', text: 'text-emerald-700', bar: 'bg-emerald-500' },
  Principal: { bg: 'bg-purple-50', text: 'text-purple-700', bar: 'bg-purple-600' },
};

const resultBadge: Record<string, { bg: string; text: string }> = {
  success: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  failure: { bg: 'bg-red-50', text: 'text-red-700' },
  pending: { bg: 'bg-amber-50', text: 'text-amber-700' },
};

const delegationStatusBadge: Record<string, { bg: string; text: string }> = {
  active: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  expired: { bg: 'bg-slate-100', text: 'text-slate-600' },
  revoked: { bg: 'bg-red-50', text: 'text-red-700' },
};

const vcStatusBadge: Record<string, { bg: string; text: string; icon: string }> = {
  active: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'verified' },
  revoked: { bg: 'bg-red-50', text: 'text-red-700', icon: 'cancel' },
  expired: { bg: 'bg-slate-100', text: 'text-slate-600', icon: 'schedule' },
};

type TabKey = 'overview' | 'delegation' | 'audit' | 'credentials';

const tabs: { key: TabKey; labelKey: string; icon: string }[] = [
  { key: 'overview', labelKey: 'detail.tabs.overview', icon: 'dashboard' },
  { key: 'delegation', labelKey: 'detail.tabs.delegation', icon: 'swap_horiz' },
  { key: 'audit', labelKey: 'detail.tabs.audit', icon: 'history' },
  { key: 'credentials', labelKey: 'detail.tabs.credentials', icon: 'verified' },
];

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

function TrustScoreBar({ score, percentile }: { score: number; percentile: number }) {
  const { t } = useTranslation('agents');
  const color = score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="rounded-xl border border-border-subtle bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-text-faint">{t('detail.overview.trustScore')}</span>
        {percentile > 0 ? (
          <span className="text-xs font-semibold text-text-muted">{t('detail.overview.topPercent', { percent: 100 - percentile })}</span>
        ) : (
          <span className="text-xs font-semibold text-text-faint">{t('detail.overview.unranked')}</span>
        )}
      </div>
      <div className="mt-3 flex items-end gap-3">
        <span className="text-4xl font-black text-text-main">{score}</span>
        <span className="mb-1 text-sm text-text-muted">{t('detail.overview.of100')}</span>
      </div>
      <div className="mt-3 h-2 w-full rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${score}%` }}></div>
      </div>
      <div className="mt-2 flex justify-between text-[10px] font-bold text-text-faint">
        <span>0</span>
        <span>{t('detail.overview.percentile', { value: percentile })}</span>
        <span>100</span>
      </div>
    </div>
  );
}

function AutonomyTier({ level, rank }: { level: string; rank: number }) {
  const { t } = useTranslation('agents');
  const config = autonomyConfig[level] || autonomyConfig.Intern;
  const tiers = ['Intern', 'Junior', 'Senior', 'Principal'];
  return (
    <div className="rounded-xl border border-border-subtle bg-white p-5 shadow-sm">
      <span className="text-xs font-bold uppercase tracking-wider text-text-faint">{t('detail.overview.autonomyLevel')}</span>
      <div className="mt-3">
        <span className={`inline-block rounded-full border px-3 py-1.5 text-sm font-bold ${config.bg} ${config.text}`}>
          {level}
        </span>
      </div>
      <div className="mt-4 flex gap-1">
        {tiers.map((tier, i) => (
          <div key={tier} className="flex-1">
            <div className={`h-1.5 rounded-full ${i <= rank ? config.bar : 'bg-slate-100'}`}></div>
            <p className={`mt-1 text-center text-[9px] font-bold uppercase ${i <= rank ? config.text : 'text-text-faint'}`}>
              {tier}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function DelegationChainVisual({ did }: { did: string }) {
  const { t } = useTranslation('agents');
  const { data: delegationChain, loading } = useAgentDelegationChain(did);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="rounded-xl border border-border-subtle bg-white p-5 shadow-sm">
      <h3 className="text-xs font-bold uppercase tracking-wider text-text-faint">{t('detail.delegation.chain')}</h3>
      <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-2">
        {(delegationChain ?? []).map((hop: Record<string, unknown>, i: number) => {
          const from = (hop.from as string) || (hop.issuer as string) || '';
          const fromDid = (hop.fromDid as string) || (hop.issuer as string) || '';
          const to = (hop.to as string) || (hop.subject as string) || '';
          const toDid = (hop.toDid as string) || (hop.subject as string) || '';
          const scope = (hop.scope as string) || (hop.capability as string) || '';
          return (
            <div key={i} className="flex items-center gap-2">
              {i === 0 && (
                <div className="flex flex-col items-center">
                  <div className="flex size-10 items-center justify-center rounded-full bg-blue-50">
                    <span className="material-symbols-outlined text-primary" style={{ fontSize: 18 }}>person</span>
                  </div>
                  <p className="mt-1 max-w-[80px] truncate text-center text-[10px] font-bold text-text-main">{from}</p>
                  <p className="max-w-[80px] truncate text-center font-mono text-[8px] text-text-faint">{fromDid}</p>
                </div>
              )}
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-1">
                  <div className="h-px w-8 bg-slate-300"></div>
                  <span className="material-symbols-outlined text-slate-400" style={{ fontSize: 14 }}>arrow_forward</span>
                  <div className="h-px w-8 bg-slate-300"></div>
                </div>
                <p className="mt-0.5 text-[8px] font-semibold text-primary">{scope}</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex size-10 items-center justify-center rounded-full bg-purple-50">
                  <span className="material-symbols-outlined text-purple-600" style={{ fontSize: 18 }}>smart_toy</span>
                </div>
                <p className="mt-1 max-w-[80px] truncate text-center text-[10px] font-bold text-text-main">{to}</p>
                <p className="max-w-[80px] truncate text-center font-mono text-[8px] text-text-faint">{toDid}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OverviewTab({ agent }: { agent: { trustScore: number; trustPercentile: number; autonomyLevel: string; autonomyRank: number; capabilities: AgentCapability[] } }) {
  const { t } = useTranslation('agents');
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <TrustScoreBar score={agent.trustScore} percentile={agent.trustPercentile} />
        <AutonomyTier level={agent.autonomyLevel} rank={agent.autonomyRank} />
      </div>

      <div>
        <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-text-faint">{t('detail.overview.capabilities')}</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {agent.capabilities.map((cap) => (
            <div key={cap.name} className="rounded-xl border border-border-subtle bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
              <div className="flex items-start gap-3">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                  <span className="material-symbols-outlined text-primary" style={{ fontSize: 18 }}>extension</span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-text-main">{cap.name}</p>
                  <p className="mt-0.5 text-xs text-text-muted">{cap.description}</p>
                  {cap.version && (
                    <span className="mt-1 inline-block rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-text-faint">
                      v{cap.version}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DelegationTab({ did }: { did: string }) {
  const { t } = useTranslation(['agents', 'common']);
  const { data: delegations, loading, error, refetch } = useAgentDelegations(did);
  const [newOpen, setNewOpen] = useState(false);
  const [actionError, setActionError] = useState('');
  const [verifyResult, setVerifyResult] = useState<{ id: string; valid: boolean; errors: string[] } | null>(null);

  // Revoking a delegation also revokes every sub-delegation made from it.
  const handleRevokeDelegation = async (delegationId: string) => {
    if (!confirm(t('detail.delegation.revokeConfirm'))) return;
    setActionError('');
    try {
      await apiFetch(`/delegations/${encodePathSegment(delegationId)}/revoke`, { method: 'POST', body: '{}' });
      refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
  };

  // Verify the whole chain behind a delegation: every signature and status,
  // narrowing, depth, and the agents and root delegator involved.
  const handleVerifyDelegation = async (delegationId: string) => {
    setActionError('');
    try {
      const res = await apiFetch<{ valid: boolean; errors?: string[] }>(`/delegations/${encodePathSegment(delegationId)}/verify`, { method: 'POST', body: '{}' });
      setVerifyResult({ id: delegationId, valid: res.valid, errors: res.errors ?? [] });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-center py-20 text-red-500">{t('common:errors.loadDelegationsFailed')}</div>;

  return (
    <div className="space-y-6">
      <DelegationChainVisual did={did} />
      <NewDelegationDialog open={newOpen} agentDid={did} onClose={() => setNewOpen(false)} onIssued={refetch} />
      {actionError && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{actionError}</div>}
      {verifyResult && (
        <div className={`rounded-lg border p-3 text-sm ${verifyResult.valid ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-700'}`}>
          <p className="font-semibold">
            {verifyResult.valid ? 'The delegation chain verifies.' : 'The delegation chain does not verify.'}{' '}
            <span className="font-mono text-xs">{verifyResult.id}</span>
          </p>
          {verifyResult.errors.map((e) => <p key={e} className="mt-1 text-xs">{e}</p>)}
        </div>
      )}

      <div className="rounded-xl border border-border-subtle bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-border-subtle px-5 py-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-text-faint">{t('detail.delegation.active')}</h3>
          <button className="btn-primary text-xs" onClick={() => setNewOpen(true)}>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>add</span>
            {t('detail.delegation.newDelegation')}
          </button>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-subtle bg-slate-50/50">
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('detail.delegation.table.delegator')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('detail.delegation.table.scope')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('detail.delegation.table.constraints')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('detail.delegation.table.expiry')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('detail.delegation.table.status')}</th>
              <th className="w-24 px-5 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {(delegations ?? []).map((d: Record<string, unknown>) => {
              const status = d.revoked ? 'revoked' : (d.status as string) || 'active';
              const badge = delegationStatusBadge[status] ?? delegationStatusBadge.active;
              const scope = Array.isArray(d.scope) ? d.scope : (d.capability ? [d.capability as string] : []);
              const constraints = (d.constraints && typeof d.constraints === 'object') ? d.constraints as Record<string, string> : {};
              const delegator = (d.delegator as string) || (d.issuer as string) || '';
              const expiry = (d.expiry as string) || (d.expiresAt as string) || '';
              const id = (d.id as string) || '';
              return (
                <tr key={id} className="border-b border-border-subtle transition-colors hover:bg-slate-50/60">
                  <td className="px-5 py-3">
                    <span className="font-mono text-xs font-semibold text-text-main">{delegator}</span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {scope.map((s: string) => (
                        <span key={s} className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-blue-700">{s}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="space-y-0.5">
                      {Object.keys(constraints).length > 0 ? (
                        Object.entries(constraints).map(([k, v]) => (
                          <p key={k} className="text-[11px] text-text-muted"><span className="font-semibold">{k}:</span> {String(v)}</p>
                        ))
                      ) : (
                        <span className="text-[11px] text-text-faint">{t('detail.delegation.noConstraints')}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-xs font-semibold text-text-main">{expiry ? new Date(expiry).toLocaleDateString() : '—'}</span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${badge.bg} ${badge.text}`}>
                      {status}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => void handleVerifyDelegation(id)} className="rounded-lg p-1 text-text-faint transition-colors hover:bg-slate-100 hover:text-text-main" title="Verify chain">
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>verified</span>
                      </button>
                      {status === 'active' && (
                        <button onClick={() => handleRevokeDelegation(id)} className="rounded-lg p-1 text-text-faint transition-colors hover:bg-red-50 hover:text-red-600" title={t('detail.delegation.table.status')}>
                          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>block</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditLogTab({ did }: { did: string }) {
  const { t } = useTranslation(['agents', 'common']);
  const { data: auditLog, loading, error, errorStatus } = useAgentAuditLog(did);

  if (loading) return <LoadingSpinner />;
  if (errorStatus === 403) {
    return (
      <div className="rounded-xl border border-border-subtle bg-slate-50/60 px-6 py-12 text-center">
        <span className="material-symbols-outlined mx-auto block text-text-faint" style={{ fontSize: 28 }}>lock</span>
        <p className="mt-2 text-sm font-bold text-text-main">{t('detail.audit.restrictedTitle')}</p>
        <p className="mt-1 mx-auto max-w-md text-xs text-text-muted">{t('detail.audit.restrictedBody')}</p>
      </div>
    );
  }
  if (error) return <div className="text-center py-20 text-red-500">{t('common:errors.loadAuditLogFailed')}</div>;

  return (
    <div className="rounded-xl border border-border-subtle bg-white shadow-sm">
      <div className="border-b border-border-subtle px-5 py-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-text-faint">{t('detail.audit.title')}</h3>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-border-subtle bg-slate-50/50">
            <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('detail.audit.table.timestamp')}</th>
            <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('detail.audit.table.action')}</th>
            <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('detail.audit.table.ibctHash')}</th>
            <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('detail.audit.table.result')}</th>
          </tr>
        </thead>
        <tbody>
          {(auditLog ?? []).map((entry: Record<string, unknown>) => {
            const result = (entry.result as string) || 'success';
            const badge = resultBadge[result] ?? resultBadge.pending;
            const timestamp = (entry.timestamp as string) || '';
            return (
              <tr key={entry.id as string} className="border-b border-border-subtle transition-colors hover:bg-slate-50/60">
                <td className="px-5 py-3">
                  <span className="text-xs font-semibold text-text-main">
                    {timestamp ? new Date(timestamp).toLocaleString() : '—'}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs font-semibold text-text-main">
                    {(entry.action as string) || ''}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <span className="font-mono text-xs text-text-muted">{(entry.ibctHash as string) || (entry.details as string) || '—'}</span>
                </td>
                <td className="px-5 py-3">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${badge.bg} ${badge.text}`}>
                    {result}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CredentialsTab({ did }: { did: string }) {
  const { t } = useTranslation(['agents', 'common']);
  const { data: credentials, loading, error } = useAgentCredentials(did);

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-center py-20 text-red-500">{t('common:errors.loadCredentialsFailed')}</div>;

  const vcList = credentials ?? [];

  return (
    <div>
      <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-text-faint">{t('detail.credentials.title')}</h3>
      {vcList.length === 0 ? (
        <div className="rounded-xl border border-border-subtle bg-white p-10 text-center">
          <span className="material-symbols-outlined text-text-faint" style={{ fontSize: 32 }}>badge</span>
          <p className="mt-2 text-sm font-semibold text-text-muted">{t('common:empty.noCredentialsIssued')}</p>
          <p className="mt-1 text-xs text-text-faint">{t('common:empty.noCredentialsHint')}</p>
        </div>
      ) : (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {vcList.map((vc: Record<string, unknown>) => {
          const status = (vc.status as string) || 'active';
          const badge = vcStatusBadge[status] ?? vcStatusBadge.active;
          const vcType = (vc.type as string) || '';
          return (
            <div key={vc.id as string} className="rounded-xl border border-border-subtle bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`flex size-10 items-center justify-center rounded-lg ${badge.bg}`}>
                    <span className={`material-symbols-outlined ${badge.text}`} style={{ fontSize: 20 }}>{badge.icon}</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-text-main">{vcType.replace('Credential', '') || t('detail.credentials.fallbackType')}</p>
                    <p className="text-[10px] font-semibold text-text-faint">ID: {vc.id as string}</p>
                  </div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${badge.bg} ${badge.text}`}>
                  {status}
                </span>
              </div>
              <div className="mt-3 space-y-1 border-t border-border-subtle pt-3">
                <p className="text-[11px] text-text-muted">
                  <span className="font-semibold">{t('detail.credentials.issuer')}</span>{' '}
                  <span className="font-mono">{(vc.issuer as string) || '—'}</span>
                </p>
                <p className="text-[11px] text-text-muted">
                  <span className="font-semibold">{t('detail.credentials.issued')}</span> {(vc.issuanceDate as string) || (vc.issuedAt as string) || '—'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

export default function AgentDetail() {
  const { did } = useParams<{ did: string }>();
  const { t } = useTranslation(['agents', 'common']);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const { data: agent, loading, error, refetch } = useAgentDetail(did ?? '');

  // Decommissioning has two halves. If the agent was registered on-chain, the
  // AgentTrustRegistry record must be decommissioned too — and the contract only
  // lets the operator wallet that registered it do so. The dialog reads the
  // chain first and offers what the connected wallet can actually do; the IDA
  // record is retired in every case.
  const { account, connect, provider } = useWeb3();
  const { decommissionAgent, isReady: walletReady } = useAgentTrustRegistry();
  // The agent lives on its operator DID's chain (key-based agent DIDs carry no
  // chain of their own); signing needs the header network to be that chain.
  const { selected, chainForDID } = useNetwork();
  const agentChain = (agent?.operator && chainForDID(agent.operator)) || selected;
  const chainReady = walletReady && (!agentChain || agentChain.chainId === selected?.chainId);
  const [decomOpen, setDecomOpen] = useState(false);
  const [chainState, setChainState] = useState<'checking' | 'unreadable' | OnChainAgent | null>('checking');
  const [decomBusy, setDecomBusy] = useState(false);
  const [decomError, setDecomError] = useState('');
  // Set when the on-chain step succeeded but the IDA step did not, so a retry
  // does not try to decommission on-chain a second time.
  const [chainTx, setChainTx] = useState('');

  const openDecommission = async () => {
    if (!did) return;
    setDecomOpen(true);
    setDecomError('');
    setChainState('checking');
    try {
      setChainState(await readOnChainAgent(did, agentChain));
    } catch {
      setChainState('unreadable');
    }
  };

  const runDecommission = async (onChain: boolean) => {
    if (!did) return;
    setDecomBusy(true);
    setDecomError('');
    let tx = chainTx;
    try {
      if (onChain && !tx) {
        tx = (await decommissionAgent(did)).txHash;
        setChainTx(tx);
      }
      await apiFetch(`/agents/${encodePathSegment(did)}${tx ? `?txHash=${tx}` : ''}`, { method: 'DELETE' });
      setDecomOpen(false);
      setChainTx('');
      refetch(); // stay on the page; it now shows the Decommissioned status
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setDecomError(
        tx
          ? `Decommissioned on-chain (tx ${tx.slice(0, 10)}…), but updating IDA failed: ${msg}. Retry to finish.`
          : await describeTxError(e, { provider, account, chain: agentChain }),
      );
    } finally {
      setDecomBusy(false);
    }
  };

  const onChainActive = chainState !== null && typeof chainState === 'object' && chainState.status !== 'decommissioned';
  const walletIsOperator =
    onChainActive && !!account && account.toLowerCase() === (chainState as OnChainAgent).operator.toLowerCase();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !agent) {
    return <div className="text-center py-20 text-red-500">{t('common:errors.loadAgentDetailsFailed')}</div>;
  }

  const st = statusConfig[agent.status] ?? statusConfig.registered;
  const statusKey = (agent.status as string) in statusConfig ? agent.status : 'registered';

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-2 text-xs text-text-faint">
        <Link to="/ai-agents" className="hover:text-primary">{t('detail.breadcrumbBase')}</Link>
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
        <span className="font-semibold text-text-main">{agent.name}</span>
      </nav>

      <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex size-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50">
              <span className="material-symbols-outlined text-primary" style={{ fontSize: 28, fontVariationSettings: "'FILL' 1" }}>smart_toy</span>
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-text-main">{agent.name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-3">
                <span className="inline-block rounded border border-slate-200 bg-slate-100 px-2 py-0.5 font-mono text-[11px] font-semibold text-text-main">
                  {agent.did}
                </span>
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide ${st.text}`}>
                  <span className={`size-1.5 rounded-full ${st.dot}`}></span>
                  {t(`statusLabels.${statusKey}`)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/agents/${did}/tools`}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-text-muted transition-colors hover:bg-slate-50 hover:text-text-main"
            >
              <span className="material-symbols-outlined mr-1 align-middle" style={{ fontSize: 14 }}>extension</span>
              {t('detail.navTools')}
            </Link>
            <Link
              to={`/agents/${did}/audit`}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-text-muted transition-colors hover:bg-slate-50 hover:text-text-main"
            >
              <span className="material-symbols-outlined mr-1 align-middle" style={{ fontSize: 14 }}>history</span>
              {t('detail.navAudit')}
            </Link>
            <Link
              to={`/agents/${did}/policies`}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-text-muted transition-colors hover:bg-slate-50 hover:text-text-main"
            >
              <span className="material-symbols-outlined mr-1 align-middle" style={{ fontSize: 14 }}>policy</span>
              {t('detail.navPolicies')}
            </Link>
            {did && (
              <AgentSuspendControl
                did={did}
                status={agent.status}
                agentChain={agentChain}
                suspensionReason={agent.suspensionReason}
                onChanged={refetch}
              />
            )}
            {agent.status !== 'decommissioned' && (
              <button onClick={openDecommission} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50">
                <span className="material-symbols-outlined mr-1" style={{ fontSize: 14 }}>power_settings_new</span>
                {t('detail.decommission')}
              </button>
            )}
          </div>
        </div>

        {agent.status === 'suspended' && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <p>{t('detail.suspendedBanner')}</p>
            {agent.suspensionReason && (
              <p className="mt-1 text-xs">
                <span className="font-semibold">{t('detail.suspensionReason')}:</span> {agent.suspensionReason}
              </p>
            )}
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-border-subtle pt-4 sm:grid-cols-4">
          <div className="min-w-0">
            <span className="text-[10px] font-bold uppercase tracking-wider text-text-faint">{t('detail.fields.operator')}</span>
            {agent.operatorName ? (
              <p className="mt-0.5 truncate text-sm font-semibold text-text-main" title={agent.operatorName}>{agent.operatorName}</p>
            ) : null}
            {agent.operator ? (
              <p className="mt-0.5 truncate font-mono text-[10px] text-text-faint" title={agent.operator}>{agent.operator}</p>
            ) : (
              <p className="mt-0.5 text-sm font-semibold text-text-faint">—</p>
            )}
          </div>
          <div className="min-w-0">
            <span className="text-[10px] font-bold uppercase tracking-wider text-text-faint">{t('detail.fields.deployed')}</span>
            <p className="mt-0.5 text-sm font-semibold text-text-main">{agent.deployedDate || '—'}</p>
          </div>
          <div className="min-w-0">
            <span className="text-[10px] font-bold uppercase tracking-wider text-text-faint">{t('detail.fields.model')}</span>
            <p className="mt-0.5 truncate text-sm font-semibold text-text-main">{agent.modelName || '—'}</p>
            <p className="truncate text-[10px] text-text-faint">{agent.modelProvider}</p>
          </div>
          <div className="min-w-0">
            <span className="text-[10px] font-bold uppercase tracking-wider text-text-faint">{t('detail.fields.trustAutonomy')}</span>
            <p className="mt-0.5 text-sm font-semibold text-text-main">
              {agent.trustScore}<span className="text-text-faint text-xs font-normal">/100</span>
              <span className="ml-2 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">{agent.autonomyLevel}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="border-b border-border-subtle">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 border-b-2 pb-2 pt-1 text-xs font-bold uppercase tracking-wider transition-colors ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-faint hover:text-text-muted'
              }`}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{tab.icon}</span>
              {t(tab.labelKey)}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && <OverviewTab agent={agent} />}
      {activeTab === 'delegation' && <DelegationTab did={did ?? ''} />}
      {activeTab === 'audit' && <AuditLogTab did={did ?? ''} />}
      {activeTab === 'credentials' && <CredentialsTab did={did ?? ''} />}

      <Modal open={decomOpen} onClose={() => { if (!decomBusy) setDecomOpen(false); }} title={t('detail.decommission')}>
        <div className="space-y-4 text-sm">
          <p className="text-text-main">
            Decommissioning is permanent. The agent's delegations and marketplace listings are revoked and it stops
            accepting trust signals. Its record and history are kept, shown as Decommissioned.
          </p>

          {chainState === 'checking' && (
            <p className="flex items-center gap-2 text-text-faint">
              <span className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Checking the agent's on-chain record…
            </p>
          )}
          {chainState === 'unreadable' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
              The blockchain could not be read, so it is unknown whether this agent is registered on-chain. You can
              decommission it in IDA now and check the chain later.
            </div>
          )}
          {chainState === null && (
            <p className="text-text-faint">This agent is not registered on-chain, so only the IDA record changes.</p>
          )}
          {typeof chainState === 'object' && chainState !== null && chainState.status === 'decommissioned' && (
            <p className="text-text-faint">Already decommissioned on-chain. Only the IDA record changes.</p>
          )}
          {onChainActive && walletIsOperator && chainReady && (
            <p className="text-text-main">
              Registered on-chain. MetaMask will ask you to sign the decommission, then IDA is updated.
            </p>
          )}
          {onChainActive && !(walletIsOperator && chainReady) && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
              <p className="font-semibold">This agent is registered on-chain.</p>
              <p className="mt-1">
                Only the wallet that registered it (
                <span className="font-mono break-all">{(chainState as OnChainAgent).operator}</span>) can decommission
                it on-chain.{' '}
                {!account
                  ? 'Connect that wallet to decommission it on-chain.'
                  : walletIsOperator
                    ? `Select ${agentChain?.name ?? 'the agent\'s network'} in the header and MetaMask to continue.`
                    : 'The connected wallet is a different one.'}{' '}
                If you continue without it, IDA marks the agent Decommissioned but the on-chain record stays Active.
              </p>
            </div>
          )}

          {decomError && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{decomError}</div>}

          <div className="flex flex-wrap justify-end gap-3">
            <button
              onClick={() => setDecomOpen(false)}
              disabled={decomBusy}
              className="rounded-lg border border-border-subtle px-4 py-2 font-medium text-text-main hover:bg-slate-50 disabled:opacity-50"
            >
              {t('common:actions.cancel')}
            </button>
            {onChainActive && !account && (
              <button onClick={() => void connect()} disabled={decomBusy} className="btn-secondary">
                Connect wallet
              </button>
            )}
            {onChainActive && walletIsOperator && chainReady ? (
              <button
                onClick={() => void runDecommission(true)}
                disabled={decomBusy}
                className="rounded-lg bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {decomBusy ? 'Working…' : chainTx ? 'Retry updating IDA' : 'Decommission on-chain and in IDA'}
              </button>
            ) : (
              <button
                onClick={() => void runDecommission(false)}
                disabled={decomBusy || chainState === 'checking'}
                className="rounded-lg bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {decomBusy ? 'Working…' : onChainActive ? 'Decommission in IDA only' : 'Decommission'}
              </button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
